import React, { useState, useEffect, useRef } from "react";
import { CgSandClock } from "react-icons/cg";
import { iconMap } from "../gameIconsUtils";
import { IoMdArrowDropleft, IoMdArrowDropright } from "react-icons/io";
import Player from "../player/Player";
import DrawHitbox from "./DrawHitbox";
import {
	managePnjBoxes,
	managePnjPath,
	managePnjTrippedItems,
} from "./safePlaceUtils";
import GameEndScreen from "./GameEndScreen";
import { playerSpriteSets } from "../player/playerIconsUtils";
import Loader from "components/shared/Loader";

const GAME_DURATION = 240; // 4 minutes in seconds
const ASPECT_RATIO = 7500 / 1888;
const REFERENCE_HEIGHT = 1888;
const REFERENCE_WIDTH = 7500; // Add a reference width for scaling

const PNJ_DELAY_AFTER_TRIP = 500; // 1-second delay after tripping
const SCROLL_AMOUNT = 200;

const _question = {
	options: {
		background: "situation_safe_place",
		items: [
			{
				id: 1,
				value: "bouche_egout",
				position: { x: 465, y: 1540 },
				size: { height: 100, width: 240 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "bouche_egout_decouverte",
				fixedImg: "bouche_egout_fermee",
			},
			{
				id: 2,
				value: "flaque_d_eau",
				position: { x: 1400, y: 1540 },
				size: { height: 100, width: 240 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "flaque_d_eau",
				fixedImg: "",
			},
			{
				id: 6,
				value: "flaque_d_eau",
				position: { x: 2360, y: 1770 },
				size: { height: 100, width: 240 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "flaque_d_eau",
				fixedImg: "",
			},
			{
				id: 3,
				value: "nid_de_poule",
				position: { x: 860, y: 1770 },
				size: { height: 100, width: 160 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "nid_de_poule",
				fixedImg: "",
			},
			{
				id: 4,
				value: "rembarde",
				position: { x: 3160, y: 1250 },
				size: { height: 600, width: 600 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "rembarde_cassee",
				fixedImg: "rembarde_reparee",
			},
			{
				id: 7,
				value: "flaque_d_eau",
				position: { x: 4278, y: 1300 },
				size: { height: 100, width: 240 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "flaque_d_eau",
				fixedImg: "",
			},
			{
				id: 8,
				value: "flaque_d_eau",
				position: { x: 6200, y: 1300 },
				size: { height: 100, width: 240 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "flaque_d_eau",
				fixedImg: "",
			},
			{
				id: 5,
				value: "echelle",
				position: { x: 5800, y: 780 },
				size: { height: 580, width: 180 },
				hint: "!!!",
				isBroken: false,
				showHint: false,
				brokenImg: "echelle",
				fixedImg: "echelle",
				brokenTransform: "rotate(30deg)",
			},
		],
	},
};

const IserbaSafePlace = ({ question, handleNext, setQuizResults }) => {
	const [items, setItems] = useState(() =>
		_question.options.items.map((item) => ({
			...item,
			isBroken: false,
			showHint: false,
		}))
	);

	const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
	const containerRef = useRef(null);
	const [isScrollable, setIsScrollable] = useState(false);

	const [worldWidth, setWorldWidth] = useState(0);
	const [screenHeight, setScreenHeight] = useState(window.innerHeight);
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);

	const aspectRatio = screenWidth / screenHeight;
	const referenceAspectRatio = REFERENCE_WIDTH / REFERENCE_HEIGHT;

	const [gameOver, setGameOver] = useState(false);
	const [gameOverTitle, setGameOverTitle] = useState("");
	const [gameOverText, setGameOverText] = useState("");

	const [score, setScore] = useState(0);
	const scoreRef = useRef(null);

	const trippingRef = useRef(new Set()); // stores pnj.id currently tripping

	const [pnjs, setPnjs] = useState([
		{
			id: 1,
			spriteSet: playerSpriteSets.man_purple,
			position: { x: 600, y: 1850 },
			size: { height: 200, width: 200 },
			direction: { dx: 1, dy: 0 },
			speed: 13,
			isTripping: false,
			canCarryBoxes: true,
			waypoints: [
				{ x: 0, y: 1850 },
				{ x: 3170, y: 1850 }, // Start climbing stairs
				{ x: 3760, y: 1410 }, // Reach top of stairs
				{ x: 3800, y: 1380 },
				{ x: 4300, y: 1380 },
				{ x: 4500, y: 1330 },
				{ x: 5500, y: 1330 },
				{ x: 5600, y: 1380 },
				{ x: 6500, y: 1380 },
				{ x: 6700, y: 1330 },
				{ x: 7200, y: 1330 },
			],
		},
		{
			id: 2,
			spriteSet: playerSpriteSets.man_purple_2,
			position: { x: 2000, y: 1600 },
			size: { height: 200, width: 200 },
			direction: { dx: 1, dy: 0 },
			speed: 15,
			isTripping: false,
			canCarryBoxes: true,
			waypoints: [
				{ x: 0, y: 1600 },
				{ x: 2800, y: 1600 },
			],
			pathDirection: "forward",
		},
		{
			id: 3,
			spriteSet: playerSpriteSets.man_turquoise,
			position: { x: 4850, y: 1330 },
			size: { height: 200, width: 200 },
			direction: { dx: -1, dy: 0 },
			speed: 14,
			isTripping: false,
			canCarryBoxes: false,
			waypoints: [
				{ x: 4800, y: 1330 },
				{ x: 4900, y: 1380 },
				{ x: 5900, y: 1380 },
				{ x: 5900, y: 780 },
				{ x: 5100, y: 780 },
			],
		},
		{
			id: 4,
			spriteSet: playerSpriteSets.woman_purple,
			position: { x: 300, y: 1600 },
			size: { height: 200, width: 200 },
			direction: { dx: 1, dy: 0 },
			speed: 13,
			isTripping: false,
			canCarryBoxes: true,
			waypoints: [
				{ x: 0, y: 1600 },
				{ x: 2800, y: 1600 },
			],
		},
		{
			id: 5,
			spriteSet: playerSpriteSets.woman_purple_2,
			position: { x: 3800, y: 1380 },
			size: { height: 200, width: 200 },
			direction: { dx: 1, dy: 0 },
			speed: 15,
			isTripping: false,
			canCarryBoxes: true,
			waypoints: [
				{ x: 0, y: 1850 },
				{ x: 3170, y: 1850 }, // Start climbing stairs
				{ x: 3760, y: 1410 }, // Reach top of stairs
				{ x: 3800, y: 1380 },
				{ x: 4300, y: 1380 },
			],
		},
		{
			id: 6,
			spriteSet: playerSpriteSets.woman_purple_3,
			position: { x: 3800, y: 1380 },
			size: { height: 200, width: 200 },
			direction: { dx: 1, dy: 0 },
			speed: 14,
			isTripping: false,
			canCarryBoxes: true,
			waypoints: [
				{ x: 3800, y: 1380 },
				{ x: 4300, y: 1380 },
				{ x: 4500, y: 1330 },
				{ x: 5500, y: 1330 },
				{ x: 5600, y: 1380 },
				{ x: 6500, y: 1380 },
				{ x: 6700, y: 1330 },
				{ x: 7200, y: 1330 },
			],
		},
	]);

	const [pickupZones, setPickupZones] = useState([
		{
			id: 1,
			position: { x: 5000, y: 1300 },
			size: { width: 110, height: 60 },
			boxes: 4,
		},
		{
			id: 2,
			position: { x: 5110, y: 1300 },
			size: { width: 110, height: 60 },
			boxes: 6,
		},
		{
			id: 3,
			position: { x: 5220, y: 1300 },
			size: { width: 110, height: 60 },
			boxes: 2,
		},
	]);

	const [dropZones, setDropZones] = useState([
		{
			id: 1,
			position: { x: 6800, y: 1310 },
			size: { width: 110, height: 60 },
			boxes: 0,
			maxBoxes: 6,
		},
		{
			id: 2,
			position: { x: 6910, y: 1310 },
			size: { width: 110, height: 60 },
			boxes: 0,
			maxBoxes: 2,
		},
		{
			id: 3,
			position: { x: 7020, y: 1310 },
			size: { width: 110, height: 60 },
			boxes: 0,
			maxBoxes: 4,
		},

		{
			id: 4,
			position: { x: 5140, y: 770 },
			size: { width: 110, height: 30 },
			boxes: 0,
			maxBoxes: 6,
		},
		{
			id: 5,
			position: { x: 5250, y: 770 },
			size: { width: 110, height: 30 },
			boxes: 0,
			maxBoxes: 2,
		},
		{
			id: 6,
			position: { x: 5360, y: 770 },
			size: { width: 110, height: 30 },
			boxes: 0,
			maxBoxes: 4,
		},
	]);

	const [spritesLoaded, setSpritesLoaded] = useState(false);

	useEffect(() => {
		const allImageUrls = [];

		Object.values(playerSpriteSets).forEach((spriteSet) => {
			Object.values(spriteSet).forEach((stateFrames) => {
				Object.values(stateFrames).forEach((frameArray) => {
					allImageUrls.push(...frameArray);
				});
			});
		});

		let loadedCount = 0;

		allImageUrls.forEach((src) => {
			const img = new Image();
			img.src = src;
			img.onload = () => {
				loadedCount++;
				if (loadedCount === allImageUrls.length) {
					setSpritesLoaded(true);
				}
			};
			img.onerror = () => {
				console.warn(`Failed to load sprite: ${src}`);
				loadedCount++;
				if (loadedCount === allImageUrls.length) {
					setSpritesLoaded(true); // still continue even with some broken
				}
			};
		});
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setScreenHeight(window.innerHeight);
			setScreenWidth(window.innerWidth);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		const calculatedWorldWidth = Math.round(screenHeight * ASPECT_RATIO);
		setWorldWidth(calculatedWorldWidth);
	}, [screenHeight]);

	useEffect(() => {
		const timer = setInterval(() => {
			setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
		}, 1000);

		// Randomly break items every 10 seconds
		const breakageInterval = setInterval(() => {
			const randomItem = items[Math.floor(Math.random() * items.length)];

			setItems((prevState) => {
				return prevState.map((item) => {
					if (item.id === randomItem.id) {
						return { ...item, isBroken: true, showHint: true };
					}
					return item;
				});
			});
		}, 10000 - (240 - timeLeft) * 20);

		return () => {
			clearInterval(timer);
			clearInterval(breakageInterval);
		};
	}, []);

	useEffect(() => {
		if (timeLeft === 0) {
			setGameOver(true);
		}
	}, [timeLeft]);

	useEffect(() => {
		const checkScrollable = () => {
			if (containerRef.current) {
				setIsScrollable(containerRef.current.scrollWidth > window.innerWidth);
			}
		};
		checkScrollable();
		window.addEventListener("resize", checkScrollable);
		return () => window.removeEventListener("resize", checkScrollable);
	}, []);

	useEffect(() => {
		const movePnjs = setInterval(() => {
			setPnjs((prevPnjs) => {
				return prevPnjs.map((pnj) => {
					if (
						pnj.isTripping ||
						pnj.isWaiting ||
						trippingRef.current.has(pnj.id)
					)
						return pnj;

					const pnjPosX = scaledX(pnj.position.x);
					const pnjPosY = scaledY(pnj.position.y);

					const {
						hasBoxes,
						inPickupZone,
						inValidDropZone,
						validPickupZone,
						validDropZone,
					} = managePnjBoxes({
						pnj,
						pnjPosX,
						pnjPosY,
						scaledX,
						scaledY,
						scaledWidth,
						scaledHeight,
						pickupZones,
						dropZones,
					});

					if (inPickupZone && hasBoxes === 0 && pnj.canCarryBoxes) {
						setTimeout(() => {
							setPnjs((pnjs) =>
								pnjs.map((p) =>
									p.id === pnj.id
										? {
												...p,
												isWaiting: false,
												boxes: Math.min(3, validPickupZone.boxes),
												speed: p.speed / validPickupZone.boxes + 1,
										  }
										: p
								)
							);

							setPickupZones((prevPickupZones) =>
								prevPickupZones.map((zone) =>
									zone.id === validPickupZone.id
										? { ...zone, boxes: Math.max(0, validPickupZone.boxes - 3) }
										: zone
								)
							);
						}, 2000);
						return { ...pnj, isWaiting: true };
					}

					if (inValidDropZone && hasBoxes >= 1) {
						setTimeout(() => {
							setPnjs((pnjs) =>
								pnjs.map((p) =>
									p.id === pnj.id
										? {
												...p,
												isWaiting: false,
												boxes: 0,
												speed: 5 + Math.random(),
												direction: { dx: Math.sign(Math.random()), dy: 0 },
										  }
										: p
								)
							);

							setDropZones((prevDropZones) =>
								prevDropZones.map((zone) =>
									zone.id === validDropZone.id
										? { ...zone, boxes: validDropZone.boxes + pnj.boxes }
										: zone
								)
							);
						}, 1000);
						return { ...pnj, isWaiting: true, direction: { dx: 0, dy: 0 } };
					}

					const { normX, normY, newIndex, pathDirection } = managePnjPath({
						pnj,
						pnjPosX,
						pnjPosY,
						scaledX,
						scaledY,
					});

					const newX = pnj.position.x + normX;
					const newY = pnj.position.y + normY;

					const newDY =
						Math.abs(newY - pnj.position.y) > 1
							? Math.sign(newY - pnj.position.y)
							: 0;
					const newDX =
						Math.abs(newX - pnj.position.x) > 1
							? Math.sign(newX - pnj.position.x)
							: 0;

					const trippedItem = managePnjTrippedItems({
						items,
						scaledX,
						scaledY,
						scaledHeight,
						scaledWidth,
						newX,
						newY,
					});

					if (
						trippedItem &&
						!trippingRef.current.has(pnj.id) &&
						!pnj.isTripping
					) {
						trippingRef.current.add(pnj.id); // track tripping in ref

						// Deduct score once
						setScore((prev) => prev - 2);

						return {
							...pnj,
							isTripping: true,
							trippedItem: trippedItem.value,
							position: { x: newX, y: newY },
							direction: { dx: newDX, dy: 0 },
							currentWaypointIndex: newIndex,
							pathDirection,
						};
					}

					// normal movement
					return {
						...pnj,
						position: { x: newX, y: newY },
						direction: { dx: newDX, dy: newDY },
						currentWaypointIndex: newIndex,
						pathDirection,
					};
				});
			});
		}, 100);

		return () => clearInterval(movePnjs);
	}, [items, worldWidth, screenHeight, screenWidth]);

	const handlePnjClick = (pnj) => {
		if (pnj.isTripping) return;
		if (pnj.isWaiting) return;
		if (pnj.boxes > 1) {
			setScore((prevScore) => prevScore + 1);
			setPnjs((prevPnjs) =>
				prevPnjs.map((p) =>
					p.id === pnj.id
						? {
								...p,
								boxes: p.boxes - 1,
								speed: 5 + Math.random() / ((p.boxes - 1) / 2),
						  }
						: p
				)
			);
			setPickupZones((prevPickupZones) =>
				prevPickupZones.concat({
					id: prevPickupZones.length + 1,
					position: {
						x: unscaledX(scaledX(pnj.position.x)),
						y: pnj.position.y - scaledHeight(70),
					},
					size: {
						width: 120,
						height: 60,
					},
					boxes: 1,
					maxBoxes: 3,
				})
			);
		}
	};

	const handleItemClick = (clickedItem) => {
		if (!clickedItem.isBroken) return;

		setScore((prevScore) => prevScore + 2);

		// 1. Update items
		setItems((prevState) =>
			prevState.map((item) =>
				item.id === clickedItem.id
					? { ...item, isBroken: false, showHint: false }
					: item
			)
		);

		// 2. Delay PNJ recovery
		setTimeout(() => {
			setPnjs((prevPnjs) =>
				prevPnjs.map((pnj) => {
					if (!pnj.isTripping) return pnj;

					// Check if the item that made the PNJ trip has just been fixed
					if (pnj.trippedItem === clickedItem.value) {
						trippingRef.current.delete(pnj.id);
						return {
							...pnj,
							isTripping: false,
							trippedItem: null,
						};
					}

					return pnj;
				})
			);
		}, PNJ_DELAY_AFTER_TRIP);
	};

	const scaledY = (positionY) => {
		return (positionY / REFERENCE_HEIGHT) * screenHeight;
	};

	const scaledX = (positionX) => {
		return aspectRatio > referenceAspectRatio
			? (positionX / REFERENCE_WIDTH) * screenWidth
			: (positionX / REFERENCE_WIDTH) * (screenHeight * referenceAspectRatio);
	};

	const unscaledX = (screenX) => {
		return aspectRatio > referenceAspectRatio
			? (screenX / screenWidth) * REFERENCE_WIDTH
			: (screenX / (screenHeight * referenceAspectRatio)) * REFERENCE_WIDTH;
	};

	const scaledHeight = (height) => {
		return (height / REFERENCE_HEIGHT) * screenHeight;
	};

	const scaledWidth = (width) => {
		return aspectRatio > referenceAspectRatio
			? (width / REFERENCE_WIDTH) * screenWidth
			: (width / REFERENCE_WIDTH) * (screenHeight * referenceAspectRatio);
	};

	const handleScrollRight = () => {
		if (containerRef.current) {
			containerRef.current.scrollBy({
				left: SCROLL_AMOUNT,
				behavior: "smooth",
			});
		}
	};

	const handleScrollLeft = () => {
		if (containerRef.current) {
			containerRef.current.scrollBy({
				left: -SCROLL_AMOUNT,
				behavior: "smooth",
			});
		}
	};

	const handleNextLevel = () => {
		// set Reposnses
		setQuizResults((prev) => ({
			...prev,
			score: prev.score + score,
			responses: {
				...prev.responses,
				[question.id]: {
					score: score,
				},
			},
		}));
		handleNext();
	};

	useEffect(() => {
		if (!scoreRef.current) return;
		scoreRef.current.classList.remove("score-up");
		void scoreRef.current.offsetWidth;
		scoreRef.current.classList.add("score-up");
	}, [score]);

	if (!spritesLoaded) {
		return <Loader />;
	}

	if (gameOver) {
		return (
			<div className="relative w-screen h-screen overflow-hidden flex flex-col non-selectable">
				<GameEndScreen
					title={gameOverTitle}
					text={gameOverText}
					levelCompleted={true}
					score={score}
					onNextLevel={handleNextLevel}
				/>
			</div>
		);
	}

	return (
		<div className="relative w-full h-full overflow-hidden flex flex-col non-selectable">
			{/* Scrollable Map Container */}
			<div
				ref={containerRef}
				className="relative w-full h-full overflow-x-auto flex overflow-y-hidden"
			>
				<div
					className="absolute top-0 left-0  bg-cover "
					style={{ width: worldWidth, height: screenHeight }}
				>
					<div
						className="absolute z-0 inset-0 bg-gradient-to-br from-blue-400 via-cyan-300 to-orange-400"
						style={{ width: worldWidth, height: screenHeight }}
					></div>
					<img
						src={iconMap[_question.options.background]}
						alt="Background"
						className="absolute z-10  object-cover"
						style={{ width: worldWidth, height: screenHeight }}
					/>
				</div>

				{items.map((item) => {
					return (
						<React.Fragment key={item.id}>
							{(item?.isBroken || (!item.isBroken && item?.fixedImg)) && (
								<img
									src={
										item?.isBroken
											? iconMap[item.brokenImg]
											: iconMap[item.fixedImg]
									}
									alt={item.value}
									className="absolute z-20 cursor-pointer"
									style={{
										left: `${scaledX(item.position.x)}px`,
										top: `${scaledY(item.position.y)}px`,
										height: scaledHeight(item.size.height),
										width: scaledWidth(item.size.width),
										transform: item.isBroken ? item.brokenTransform || "" : "",
									}}
									onClick={() => handleItemClick(item)}
								/>
							)}

							{item?.showHint && (
								<div
									className="absolute z-30 text-sm py-1 px-2 shadow-md bg-white rounded-3xl"
									style={{
										left: `${
											scaledX(item.position.x) +
											scaledWidth(item.size.width) / 2 -
											15
										}px`,
										top: `${scaledY(item.position.y) - 40}px`,
									}}
								>
									<div className="flex gap-1 animated-exclamation-text font-orbitron text-xl font-bold text-red-500">
										<span>!</span> <span>!</span> <span>!</span>
									</div>
								</div>
							)}
							<DrawHitbox
								rawX={scaledX(item.position.x)}
								rawY={scaledY(item.position.y)}
								x={scaledX(item.position.x)}
								y={scaledY(item.position.y)}
								width={scaledWidth(item.size.width)}
								height={scaledHeight(item.size.height)}
								color={"red"}
							/>
						</React.Fragment>
					);
				})}

				{pnjs.map((pnj) => (
					<React.Fragment key={pnj.id}>
						{pnj.boxes > 0 &&
							[...Array(pnj.boxes)].map((_, i) => (
								<img
									key={`box-${pnj.id}-${i}`}
									src={iconMap["carton"]}
									alt="box"
									className="absolute z-40 cursor-pointer"
									onClick={() => handlePnjClick(pnj)}
									style={{
										left: `${scaledX(pnj.position.x) - scaledWidth(120) / 2}px`,
										top: `${
											scaledY(pnj.position.y) -
											scaledHeight(pnj.size.height) / 2 -
											scaledHeight(60) * i -
											5
										}px`,
										height: scaledHeight(60),
										width: scaledWidth(120),
									}}
								/>
							))}

						{pnj.boxes > 1 && (
							<div
								className="absolute z-30 text-sm py-1 px-2 shadow-md bg-white rounded-3xl"
								style={{
									left: `${scaledX(pnj.position.x) - scaledWidth(120) / 2}px`,
									top: `${
										scaledY(pnj.position.y) -
										scaledHeight(pnj.size.height) / 2 -
										(pnj.boxes + 1) * scaledHeight(60)
									}px`,
								}}
							>
								<div className="flex gap-1 animated-exclamation-text font-orbitron text-xl font-bold text-red-500">
									<span>!</span> <span>!</span> <span>!</span>
								</div>
							</div>
						)}
						<Player
							position={{
								x: scaledX(pnj.position.x),
								y: scaledY(pnj.position.y),
							}}
							direction={
								pnj.isWaiting || pnj.isTripping
									? { dx: 0, dy: 0 }
									: pnj.direction
							}
							isFalling={pnj.isTripping}
							height={scaledHeight(pnj.size.height) + "px"}
							width={scaledWidth(pnj.size.width) + "px"}
							spriteSet={pnj.spriteSet}
						/>
					</React.Fragment>
				))}

				{/* {pnjs.map((pnj) => {
					return pnj.waypoints.map((wp, i) => {
						return (
							<div
								key={`waypoint-${pnj.id}-${i}`}
								className="absolute z-30 w-2 h-2 bg-green-500"
								style={{
									left: `${scaledX(wp.x)}px`,
									top: `${scaledY(wp.y)}px`,
								}}
								title={`PNJ ${pnj.id} WP ${i}`}
							></div>
						);
					});
				})} */}

				{pickupZones.map((pickupZone) => {
					return (
						<>
							{pickupZone.boxes > 0 &&
								[...Array(pickupZone.boxes)].map((_, i) => (
									<img
										src={iconMap["carton"]}
										alt={"box"}
										key={`box-${pickupZone.id}-${i}`}
										className="absolute z-20 cursor-pointer"
										style={{
											left: `${scaledX(pickupZone.position.x)}px`,
											top: `${
												scaledY(pickupZone.position.y) -
												scaledHeight(60) -
												scaledHeight(60) * i
											}px`,
											height: scaledHeight(60),
											width: scaledWidth(120),
										}}
									/>
								))}

							<DrawHitbox
								x={scaledX(pickupZone.position.x)}
								y={scaledY(pickupZone.position.y)}
								width={scaledWidth(pickupZone.size.width)}
								height={scaledHeight(pickupZone.size.height)}
								color="pink"
							/>
						</>
					);
				})}

				{dropZones.map((dropZone) => {
					return (
						<>
							{dropZone.boxes > 0 &&
								[...Array(dropZone.boxes)].map((_, i) => (
									<img
										src={iconMap["carton"]}
										alt={"box"}
										key={`box-${dropZone.id}-${i}`}
										className="absolute z-40 cursor-pointer"
										style={{
											left: `${scaledX(dropZone.position.x)}px`,
											top: `${
												scaledY(dropZone.position.y) -
												scaledHeight(60) -
												scaledHeight(60) * i
											}px`,
											height: scaledHeight(60),
											width: scaledWidth(120),
										}}
									/>
								))}

							<DrawHitbox
								x={scaledX(dropZone.position.x)}
								y={scaledY(dropZone.position.y)}
								width={scaledWidth(dropZone.size.width)}
								height={scaledHeight(dropZone.size.height)}
								color="purple"
							/>
						</>
					);
				})}
			</div>

			{isScrollable && (
				<div
					className="absolute z-50 top-1/2  left-2 transform -translate-y-1/2 p-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300 rounded-full"
					onClick={handleScrollLeft}
				>
					<IoMdArrowDropleft size={28} />
				</div>
			)}
			{isScrollable && (
				<div
					className="absolute z-50 top-1/2 right-2 transform -translate-y-1/2 p-2 border-black text-white bg-[#fbb554] shadow-[6px_6px_0px_rgba(0,0,0,0.7)] hover:bg-[#ffa256] disabled:bg-slate-300 rounded-full"
					onClick={handleScrollRight}
				>
					<IoMdArrowDropright size={28} />
				</div>
			)}
			<div className="absolute z-50 top-4 left-4 w-40 flex gap-2 items-center bg-white p-2 px-4  rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] font-orbitron text-lg font-bold text-gray-600">
				Score :{" "}
				<span ref={scoreRef} className="text-[#fbb554]">
					{score}
				</span>
			</div>
			<div className="absolute z-50 top-4 right-4 flex gap-2 items-center w-32 bg-white p-2 px-4  rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,0.3)] font-orbitron text-lg font-bold text-[#fbb554]">
				<CgSandClock size={28} />
				<span className="text-gray-600">
					{Math.floor(timeLeft / 60)}:
					{(timeLeft % 60).toString().padStart(2, "0")}
				</span>
			</div>
		</div>
	);
};

export default IserbaSafePlace;
